import React, { LegacyRef, useCallback, useEffect, useRef } from 'react';

import { Pressable, Text, View } from 'react-native';

import CheckmarkIcon from 'assets/images/svgs/ico_checkmark.svg';

import setAccessibilityFocusTo from 'services/accessibility/setAccessibilityFocusTo';

import { COLOR } from 'utils/colors';
import { isTablet, isWeb } from 'utils/responsiveness';

import { ListItem } from 'components/PickerList/types';

import styles from './styles';

type Props<T extends ListItem> = {
    item: T;
    onPress(item: T): void;
    testID?: string;
    screenReaderEnabled?: boolean;
};

function BottomSheetRowItem<T extends ListItem>({
    item,
    onPress,
    testID,
    screenReaderEnabled,
}: Props<T>) {
    const pressableRef = useRef(undefined);

    const handlePress = useCallback(() => {
        onPress(item);
    }, [onPress, item]);

    useEffect(() => {
        if (item.isSelected && isTablet && screenReaderEnabled) {
            setTimeout(() => setAccessibilityFocusTo(pressableRef), 500);
        } else if (item.isSelected && isWeb) {
            setAccessibilityFocusTo(pressableRef);
        }
    }, [item.isSelected, screenReaderEnabled]);

    return (
        <Pressable
            style={[styles.container, item.isSelected && styles.selectedItem]}
            onPress={handlePress}
            accessibilityLabel={item.label}
            aria-selected={item.isSelected}
            testID={testID}
            ref={pressableRef as unknown as LegacyRef<View>}
            accessibilityRole="button"
        >
            <View style={styles.titleContainer}>
                <Text
                    style={[
                        styles.title,
                        item.isSelected && styles.selectedTitle,
                    ]}
                    numberOfLines={2}
                    aria-hidden={isWeb}
                >
                    {item.label}
                </Text>
            </View>
            <View
                style={[
                    styles.checkMark,
                    item.isSelected && styles.checkMarkVisisble,
                ]}
                aria-hidden={isWeb || !item.isSelected}
            >
                <CheckmarkIcon fill={COLOR.background} />
            </View>
        </Pressable>
    );
}

export default BottomSheetRowItem;
